import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Star } from "lucide-react";
import { Empty, Field, PageHeader, PrimaryBtn, inputCls, Opt } from "@/components/Common";
import { useCities } from "@/components/HeroSearch";
import { BookingDialog } from "@/components/BookingDialog";
import { http, inr, today } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=1600&q=80";
const nightsBetween = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));

export default function Hotels() {
  const [sp] = useSearchParams();
  const { data: cities = [] } = useCities();
  const [q, setQ] = useState({ city: sp.get("city") || "", checkin: sp.get("checkin") || today(7), checkout: sp.get("checkout") || today(9), rooms: 1, guests: 2 });
  const [stars, setStars] = useState(0);
  const [selected, setSelected] = useState(null);
  const { data = [], isLoading } = useQuery({
    queryKey: ["hotels", q.city],
    queryFn: () => http.get(`/hotels/search${q.city ? `?city=${encodeURIComponent(q.city)}` : ""}`).then((r) => r.data),
  });
  const nights = nightsBetween(q.checkin, q.checkout);
  const shown = data.filter((h) => h.stars >= stars);
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });

  return (
    <div data-testid="hotels-page">
      <PageHeader eyebrow="Hotels" title="Stays worth the journey." sub="From heritage havelis in Jaipur to overwater villas in the Maldives." image={IMG} />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="-mt-10 relative grid gap-3 rounded-2xl bg-white p-5 shadow-xl sm:grid-cols-3 lg:grid-cols-5 items-end">
          <Field label="City">
            <select className={inputCls} value={q.city} onChange={set("city")} data-testid="hotel-city-select">
              <option value="">All cities</option>{cities.map((c) => <Opt key={c} v={c} l={c} />)}
            </select>
          </Field>
          <Field label="Check-in"><input type="date" min={today()} className={inputCls} value={q.checkin} onChange={set("checkin")} data-testid="hotel-checkin-input" /></Field>
          <Field label="Check-out"><input type="date" min={q.checkin} className={inputCls} value={q.checkout} onChange={set("checkout")} data-testid="hotel-checkout-input" /></Field>
          <Field label="Rooms"><input type="number" min={1} max={10} className={inputCls} value={q.rooms} onChange={(e) => setQ({ ...q, rooms: Math.max(1, +e.target.value) })} data-testid="hotel-rooms-input" /></Field>
          <Field label="Min. stars">
            <select className={inputCls} value={stars} onChange={(e) => setStars(+e.target.value)} data-testid="hotel-stars-filter">
              <option value={0}>Any</option><option value={3}>3★ +</option><option value={4}>4★ +</option><option value={5}>5★</option>
            </select>
          </Field>
        </div>
        <p className="mt-10 text-sm text-slate-500" data-testid="hotel-results-count">{shown.length} hotels · {nights} night(s)</p>
        {nights < 1 && <p className="mt-2 text-sm text-red-600" data-testid="hotel-date-error">Check-out must be after check-in.</p>}
        <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && [1, 2, 3].map((i) => <div key={i} className="h-96 animate-pulse rounded-2xl bg-white" />)}
          {shown.map((h, i) => (
            <div key={h.id} className="zoom lift overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`hotel-card-${h.id}`}>
              <div className="relative h-52 overflow-hidden">
                <img src={h.image} alt={h.name} className="h-full w-full object-cover" loading="lazy" />
                <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold">
                  {Array.from({ length: h.stars }).map((_, k) => <Star key={k} className="h-3 w-3 fill-[#D4AF37] text-[#D4AF37]" />)}
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-2xl font-bold leading-tight">{h.name}</h3>
                  <span className="rounded-md bg-[#0D1B2A] px-2 py-1 text-xs font-bold text-white">{h.rating}</span>
                </div>
                <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{h.city}, {h.country}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">{h.amenities.map((a) => <span key={a} className="rounded-full bg-[#F4F0EA] px-2.5 py-0.5 text-xs">{a}</span>)}</div>
                <p className="mt-3 text-xs font-medium text-emerald-700">{h.free_cancellation ? "Free cancellation up to 48h" : "Non-refundable rate"}</p>
                <div className="mt-4 flex items-end justify-between border-t border-[#E5E0D8] pt-4">
                  <div><p className="text-xl font-bold" data-testid={`hotel-price-${h.id}`}>{inr(h.price_per_night)}</p><p className="text-xs text-slate-500">per night</p></div>
                  <PrimaryBtn className="h-10 px-5" disabled={nights < 1} onClick={() => setSelected(h)} data-testid={`hotel-book-btn-${h.id}`}>Book room</PrimaryBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
        {!isLoading && !shown.length && <Empty testid="hotel-empty">No hotels match these filters.</Empty>}
      </div>
      {selected && (
        <BookingDialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)} title={selected.name}
          amount={selected.price_per_night * nights * q.rooms}
          lines={[["City", selected.city], ["Check-in", q.checkin], ["Check-out", q.checkout], ["Nights × rooms", `${nights} × ${q.rooms}`], ["Per night", inr(selected.price_per_night)]]}
          booking={{ type: "hotel", item_id: selected.id, travel_date: q.checkin, end_date: q.checkout, rooms: q.rooms, travellers: q.guests }} />
      )}
    </div>
  );
}
